import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Building2, Plus, Users, Headphones, PhoneCall, RefreshCw, AlertTriangle,
  PauseCircle, PlayCircle, LogIn, BarChart2, ChevronDown, ChevronUp, Palette,
  Upload, ClipboardList, FileText, TrendingUp, Activity, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

// ─── tiny utility ─────────────────────────────────────────────────────────
const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const ACTION_LABELS = {
  company_created: { label: 'Company created', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  company_suspended: { label: 'Company suspended', color: 'text-amber-600', bg: 'bg-amber-50' },
  company_reactivated: { label: 'Company reactivated', color: 'text-blue-600', bg: 'bg-blue-50' },
  user_created: { label: 'User created', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  user_deleted: { label: 'User deleted', color: 'text-rose-600', bg: 'bg-rose-50' },
};

// ─── Analytics mini-panel ─────────────────────────────────────────────────
const CompanyAnalytics = ({ companyId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/companies/${companyId}/analytics`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) return <div className="py-4 text-center text-sm text-slate-400">Loading analytics…</div>;
  if (!data) return null;

  const stats = [
    { label: 'Total Calls', value: data.calls_total, sub: `${data.calls_this_month} this month`, icon: PhoneCall, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Assessments', value: data.assessments_total, sub: `${data.assessments_this_month} this month`, icon: TrendingUp, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Avg Score', value: data.avg_score != null ? `${data.avg_score}%` : 'N/A', sub: 'all-time', icon: BarChart2, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Active Agents', value: data.active_agents, sub: `${data.total_users} total users`, icon: Headphones, color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'SOPs', value: data.sop_count, sub: 'procedures', icon: FileText, color: 'text-teal-600', bg: 'bg-teal-50' },
    { label: 'Credits', value: data.credits_balance, sub: 'remaining', icon: Activity, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  ];

  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Analytics</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(s => (
          <div key={s.label} className={`p-3 rounded-lg ${s.bg}`}>
            <s.icon size={16} className={`${s.color} mb-1`} />
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-slate-600">{s.label}</p>
            <p className="text-xs text-slate-400">{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const CompaniesConsole = () => {
  const { user, switchWorkspace } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('companies'); // 'companies' | 'audit'
  const [expandedAnalytics, setExpandedAnalytics] = useState({});

  // Create company
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', admin_name: '', admin_email: '', admin_password: '' });

  // Branding modal
  const [brandingCompany, setBrandingCompany] = useState(null);
  const [brandColor, setBrandColor] = useState('#6366f1');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const logoInputRef = useRef();

  // Audit log
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState('');

  useEffect(() => {
    if (user?.is_super_admin) fetchCompanies();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'audit' && auditLogs.length === 0) fetchAuditLogs();
  }, [activeTab]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
    } catch {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-logs?limit=200`);
      setAuditLogs(res.data);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.name || !newCompany.admin_email || !newCompany.admin_password || !newCompany.admin_name) {
      toast.error('All fields are required'); return;
    }
    try {
      await axios.post(`${API}/companies`, newCompany);
      toast.success(`${newCompany.name} created`);
      setIsCreateOpen(false);
      setNewCompany({ name: '', admin_name: '', admin_email: '', admin_password: '' });
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleToggleStatus = async (company) => {
    const newStatus = company.status === 'active' ? 'suspended' : 'active';
    try {
      await axios.put(`${API}/companies/${company.id}`, { status: newStatus });
      toast.success(`${company.name} ${newStatus === 'active' ? 'reactivated' : 'suspended'}`);
      fetchCompanies();
      if (activeTab === 'audit') { setAuditLogs([]); fetchAuditLogs(); }
    } catch {
      toast.error('Failed to update company status');
    }
  };

  const openBranding = (c) => {
    setBrandingCompany(c);
    setBrandColor(c.brand_color || '#6366f1');
    setLogoFile(null);
    setLogoPreview(c.logo_path ? `${API}/companies/${c.id}/logo` : null);
  };

  const handleLogoChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const handleSaveBranding = async () => {
    if (!brandingCompany) return;
    setBrandingSaving(true);
    try {
      if (logoFile) {
        const fd = new FormData();
        fd.append('file', logoFile);
        await axios.post(`${API}/companies/${brandingCompany.id}/logo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      await axios.put(`${API}/companies/${brandingCompany.id}`, { brand_color: brandColor });
      toast.success('Branding saved');
      setBrandingCompany(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save branding');
    } finally {
      setBrandingSaving(false);
    }
  };

  const toggleAnalytics = (id) =>
    setExpandedAnalytics(prev => ({ ...prev, [id]: !prev[id] }));

  const filteredLogs = auditFilter
    ? auditLogs.filter(l =>
        l.action?.includes(auditFilter) ||
        l.actor_email?.includes(auditFilter) ||
        l.target_name?.includes(auditFilter)
      )
    : auditLogs;

  if (!user?.is_super_admin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Card className="p-8 text-center">
            <AlertTriangle size={48} className="mx-auto text-rose-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600">Only the platform owner can access the Companies console.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="companies-console">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Companies
            </h1>
            <p className="text-slate-600">Every subscribing company on the platform</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setActiveTab('audit'); fetchAuditLogs(); }}
              className={activeTab === 'audit' ? 'bg-slate-100 border-slate-400' : ''}
              data-testid="audit-log-tab"
            >
              <ClipboardList size={16} className="mr-2" />
              Audit Log
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveTab('companies')}
              className={activeTab === 'companies' ? 'bg-slate-100 border-slate-400' : ''}
            >
              <Building2 size={16} className="mr-2" />
              Companies
            </Button>
            {activeTab === 'companies' && (
              <Button
                onClick={() => setIsCreateOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-company-btn"
              >
                <Plus size={18} className="mr-2" />
                New Company
              </Button>
            )}
          </div>
        </div>

        {/* ── Companies Tab ── */}
        {activeTab === 'companies' && (
          loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-slate-400" size={24} />
            </div>
          ) : (
            <div className="grid gap-4">
              {companies.map(c => (
                <Card key={c.id} className="p-6 border border-slate-200" data-testid={`company-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {/* Company logo or colour swatch */}
                        {c.logo_path ? (
                          <img
                            src={`${API}/companies/${c.id}/logo`}
                            alt={c.name}
                            className="w-8 h-8 rounded object-contain"
                          />
                        ) : (
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                            style={{ background: c.brand_color || '#6366f1' }}
                          >
                            {c.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <h3 className="text-lg font-bold text-slate-900">{c.name}</h3>
                        {c.is_default && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">Default</span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          c.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'
                        }`}>{c.status}</span>
                      </div>
                      {c.admin_email && <p className="text-sm text-slate-600 mb-3">Admin: {c.admin_email}</p>}
                      <div className="grid grid-cols-5 gap-3 max-w-lg text-sm">
                        <div className="flex items-center gap-1.5"><Users size={14} className="text-blue-500" /><span className="text-slate-700">{c.user_count} users</span></div>
                        <div className="flex items-center gap-1.5"><Headphones size={14} className="text-purple-500" /><span className="text-slate-700">{c.agent_count} agents</span></div>
                        <div className="flex items-center gap-1.5"><PhoneCall size={14} className="text-emerald-500" /><span className="text-slate-700">{c.call_count} calls</span></div>
                        <div className="flex items-center gap-1.5"><FileText size={14} className="text-teal-500" /><span className="text-slate-700">{c.sop_count} SOPs</span></div>
                        <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-violet-500" /><span className="text-slate-700">{c.assessment_count} assessed</span></div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4 flex-wrap justify-end">
                      <Button size="sm" variant="outline" onClick={() => toggleAnalytics(c.id)} data-testid={`analytics-${c.id}`}>
                        <BarChart2 size={14} className="mr-1" />
                        {expandedAnalytics[c.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openBranding(c)} data-testid={`branding-${c.id}`}>
                        <Palette size={14} className="mr-1" />
                        Branding
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => switchWorkspace(c.id === user.home_company_id ? null : c.id)}
                        data-testid={`view-workspace-${c.id}`}
                      >
                        <LogIn size={14} className="mr-1" />
                        View Workspace
                      </Button>
                      {!c.is_default && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => handleToggleStatus(c)}
                          className={c.status === 'active' ? 'border-amber-300 text-amber-700' : 'border-emerald-300 text-emerald-700'}
                          data-testid={`toggle-status-${c.id}`}
                        >
                          {c.status === 'active' ? <PauseCircle size={14} className="mr-1" /> : <PlayCircle size={14} className="mr-1" />}
                          {c.status === 'active' ? 'Suspend' : 'Reactivate'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {expandedAnalytics[c.id] && <CompanyAnalytics companyId={c.id} />}
                </Card>
              ))}
              {companies.length === 0 && (
                <Card className="p-12 text-center">
                  <Building2 size={48} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600">No companies yet</p>
                </Card>
              )}
            </div>
          )
        )}

        {/* ── Audit Log Tab ── */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Input
                placeholder="Filter by action, actor, or company…"
                value={auditFilter}
                onChange={e => setAuditFilter(e.target.value)}
                className="max-w-xs"
                data-testid="audit-filter-input"
              />
              <Button variant="outline" size="sm" onClick={() => { setAuditLogs([]); fetchAuditLogs(); }}>
                <RefreshCw size={14} className="mr-1" />
                Refresh
              </Button>
            </div>

            {auditLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin text-slate-400" size={24} />
              </div>
            ) : filteredLogs.length === 0 ? (
              <Card className="p-12 text-center">
                <ClipboardList size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">No audit events recorded yet</p>
              </Card>
            ) : (
              <Card className="border border-slate-200" data-testid="audit-log-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Time</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Action</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Actor</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Target</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map(log => {
                        const meta = ACTION_LABELS[log.action] || { label: log.action, color: 'text-slate-600', bg: 'bg-slate-50' };
                        return (
                          <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${log.id}`}>
                            <td className="py-3 px-4 text-slate-500 whitespace-nowrap">{fmtTime(log.timestamp)}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.color}`}>{meta.label}</span>
                            </td>
                            <td className="py-3 px-4 text-slate-700">{log.actor_email}</td>
                            <td className="py-3 px-4 text-slate-700">
                              <span className="font-medium">{log.target_name}</span>
                              <span className="ml-1 text-xs text-slate-400">({log.target_type})</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ── Create Company Dialog ── */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="max-w-md" data-testid="create-company-dialog">
            <DialogHeader><DialogTitle>Create New Company</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Company Name *</Label>
                <Input placeholder="Acme Corp" value={newCompany.name}
                  onChange={e => setNewCompany({ ...newCompany, name: e.target.value })} data-testid="company-name-input" />
              </div>
              <div>
                <Label>First Admin's Name *</Label>
                <Input placeholder="Jane Doe" value={newCompany.admin_name}
                  onChange={e => setNewCompany({ ...newCompany, admin_name: e.target.value })} data-testid="company-admin-name-input" />
              </div>
              <div>
                <Label>First Admin's Email *</Label>
                <Input type="email" placeholder="admin@acme.com" value={newCompany.admin_email}
                  onChange={e => setNewCompany({ ...newCompany, admin_email: e.target.value })} data-testid="company-admin-email-input" />
              </div>
              <div>
                <Label>First Admin's Password *</Label>
                <Input type="password" placeholder="••••••••" value={newCompany.admin_password}
                  onChange={e => setNewCompany({ ...newCompany, admin_password: e.target.value })} data-testid="company-admin-password-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleCreateCompany} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-create-company">
                  Create Company
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Branding Dialog ── */}
        <Dialog open={!!brandingCompany} onOpenChange={v => !v && setBrandingCompany(null)}>
          <DialogContent className="max-w-md" data-testid="branding-dialog">
            <DialogHeader>
              <DialogTitle>Branding — {brandingCompany?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              {/* Logo upload */}
              <div>
                <Label>Company Logo</Label>
                <div className="mt-2 flex items-center gap-4">
                  {logoPreview ? (
                    <img src={logoPreview} alt="logo" className="w-16 h-16 rounded-lg object-contain border border-slate-200" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                      <Building2 size={24} className="text-slate-400" />
                    </div>
                  )}
                  <div>
                    <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} data-testid="upload-logo-btn">
                      <Upload size={14} className="mr-1" />
                      {logoPreview ? 'Change logo' : 'Upload logo'}
                    </Button>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG, WEBP or SVG</p>
                  </div>
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                </div>
              </div>

              {/* Brand colour */}
              <div>
                <Label>Brand Colour</Label>
                <div className="mt-2 flex items-center gap-3">
                  <input
                    type="color"
                    value={brandColor}
                    onChange={e => setBrandColor(e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer border border-slate-200"
                    data-testid="brand-color-picker"
                  />
                  <Input
                    value={brandColor}
                    onChange={e => setBrandColor(e.target.value)}
                    className="w-32 font-mono text-sm"
                    placeholder="#6366f1"
                    data-testid="brand-color-input"
                  />
                  <div className="w-10 h-10 rounded-lg" style={{ background: brandColor }} />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setBrandingCompany(null)} className="flex-1">Cancel</Button>
                <Button
                  onClick={handleSaveBranding}
                  disabled={brandingSaving}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-branding-btn"
                >
                  {brandingSaving ? 'Saving…' : 'Save Branding'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CompaniesConsole;
